/**
 * Secondary IP stream page content: semantics + FAQ + decision-support data
 * plus the thin builder that runs the shared generator and attaches them.
 */
import { buildStreamPageContent } from './secondaryStreamGenerator';
import type { DecisionProofPoint, FaqItem, LevelPageContent } from './types';

const IP_SEMANTICS: Record<string, any> = {
  keySuffix: 'IP',
  streamName: 'IP programme',
  seoLabel: 'IP',
  eyebrowLabel: 'IP MATHEMATICS',
  lower: {
    seoDescription:
      'The Lower Secondary IP Mathematics syllabus as top IP schools broadly run it, topic by topic — plus weekly live classes that keep pace with an accelerated curriculum.',
    headline:
      'Keep pace with accelerated learning while deepening understanding.',
    subheadFocus:
      'conceptual depth, abstraction, and transfer across unfamiliar problem contexts.',
    syllabusBody:
      'Lessons cover the full IP Lower Secondary scope at the pace IP schools demand, with the enrichment they layer on top of the standard syllabus treated as core content.',
    syllabusTopics: [
      {
        category: 'Accelerated Algebra & Functions',
        items: [
          'Advanced manipulation and function behavior',
          'Structured equation-solving across contexts',
          'Graph interpretation at increasing complexity',
        ],
      },
      {
        category: 'Geometry, Trigonometry & Extensions',
        items: [
          'Higher-rigor geometric reasoning',
          'Coordinate and trigonometric applications',
          'Extension questions beyond standard pacing',
        ],
      },
      {
        category: 'Enrichment Problem Solving',
        items: [
          'Non-routine and Olympiad-style reasoning',
          'Data and probability in unfamiliar settings',
          'Multi-step synthesis across topics',
        ],
      },
    ],
    syllabusNote:
      'Lower Secondary IP students need both pace and depth. We support both without compromising conceptual clarity.',
    challengeFocusTitle: 'Managing accelerated pace and depth',
    challengeFocusDescription:
      'IP syllabi move quickly and demand more reasoning than the mainstream syllabus, so gaps can compound if not addressed immediately.',
    streamSupportTitle: 'Targeted support for IP-level demands',
    streamSupportDescription:
      'DeepThink identifies exactly where support is needed and closes gaps quickly while maintaining momentum.',
    progressionGoal:
      'sustain strong momentum into senior IP years and advanced topics.',
  },
  upper: {
    seoDescription:
      'The Upper Secondary IP Mathematics syllabus as top IP schools broadly run it, topic by topic — plus weekly live classes and A-Level preparation from Year 3.',
    headline: 'Prepare for advanced IP assessments and A-Level pathways.',
    subheadFocus:
      'high-rigor problem solving, proof-oriented reasoning, and pre-university readiness.',
    syllabusBody:
      'Teaching aligns with demanding school assessments while building durable foundations for H2/H3 Mathematics.',
    syllabusTopics: [
      {
        category: 'Advanced Functions & Calculus Foundations',
        items: [
          'Higher-order function reasoning',
          'Differentiation and integration readiness',
          'Algebraic fluency for pre-university pathways',
        ],
      },
      {
        category: 'High-Rigor Problem Solving',
        items: [
          'Proof-style arguments and structured reasoning',
          'Complex multi-step modelling tasks',
          'Integration of ideas across chapters',
        ],
      },
      {
        category: 'A-Level Readiness Strategy',
        items: [
          'Bridging to H2/H3 expectations',
          'Assessment strategy for intensive school papers',
          'Long-horizon revision and retention planning',
        ],
      },
    ],
    syllabusNote:
      'Upper Secondary IP preparation should strengthen both present performance and future A-Level capability.',
    challengeFocusTitle: 'Balancing depth, pace, and high expectations',
    challengeFocusDescription:
      'Students must sustain performance across demanding assessments while preparing for more advanced mathematics.',
    streamSupportTitle:
      'Strategic prioritisation with deep conceptual coaching',
    streamSupportDescription:
      'We help students focus on high-leverage topics, strengthen weak links fast, and maintain rigorous thinking.',
    progressionGoal:
      'enter pre-university mathematics with confidence, depth, and strategic clarity.',
  },
};

const IP_FAQS: Record<string, FaqItem[]> = {
  '1-ip': [
    {
      question: 'How does Sec 1 IP Math differ from mainstream G3?',
      answer:
        'IP Sec 1 typically covers similar foundational topics but at a faster pace, with more emphasis on conceptual depth, non-routine problems, and mathematical reasoning. Some schools introduce topics normally seen in Sec 2.',
    },
    {
      question:
        'My child just entered an IP school — when should they start tuition?',
      answer:
        'The first term is the best time to assess fit. If your child is keeping up comfortably, they may not need support. If they are spending excessive time on homework or falling behind class pace, starting early prevents gaps from compounding.',
    },
    {
      question: 'Does each IP school follow the same Sec 1 Math syllabus?',
      answer:
        "No. IP schools design their own syllabi, so content and pacing vary. DeepThink adapts support to each school's scheme of work rather than following a generic curriculum.",
    },
    {
      question: 'Is IP Math harder than G3 in terms of content?',
      answer:
        'The content is similar at the Sec 1 level, but IP schools often expect deeper understanding, independent reasoning, and comfort with unfamiliar problem types. The cognitive demand is higher even when the topics look familiar.',
    },
    {
      question:
        "Can DeepThink support my child's specific IP school curriculum?",
      answer:
        'Yes. We adapt to individual school syllabi and pacing. Students can bring school materials and assessments so we target exactly what they need for their specific programme.',
    },
    {
      question: 'Does the SEC exam affect IP students?',
      answer:
        'IP students bypass the national SEC exam (which replaces all three national secondary exams — O-Level, N(A)-Level, N(T)-Level — from 2027) and proceed directly to A-Levels or the IB. Their internal school assessments serve the same gatekeeping function — Sec 1 IP performance shapes Upper Secondary subject options.',
    },
  ],
  '2-ip': [
    {
      question: 'What does Sec 2 IP Math typically cover?',
      answer:
        'Sec 2 IP typically covers quadratic equations, simultaneous equations, function notation, advanced coordinate geometry, trigonometry (including sine and cosine rules), circle properties, cumulative frequency, and probability models.',
    },
    {
      question: 'Is Sec 2 a critical year for IP Math students?',
      answer:
        'Yes. Many IP schools use Sec 2 assessments to determine subject combinations and pacing for Upper Secondary. Students who fall behind in Sec 2 may face restricted options or difficulty keeping up with Sec 3 content.',
    },
    {
      question:
        'My child is managing Sec 2 IP but not excelling — is that a concern?',
      answer:
        'It depends on the trajectory. If marks are stable, the student may simply need more time with the material. If marks are declining, it signals compounding gaps that become harder to fix in Sec 3 when the pace accelerates further.',
    },
    {
      question: 'How does Sec 2 IP compare to Sec 2 G3 in difficulty?',
      answer:
        'IP schools typically cover the same Sec 2 topics as G3 but move faster and go deeper. Some schools also introduce Sec 3 topics early. The main difference is pace and the expectation for independent mathematical thinking.',
    },
    {
      question:
        'Should my Sec 2 IP child be preparing for A-Math content already?',
      answer:
        "Some IP schools begin introducing A-Math concepts in Sec 2. Even if your child's school has not started, building strong algebraic fluency now makes the transition to formal A-Math topics in Sec 3 significantly smoother.",
    },
    {
      question: 'Does the new SEC exam affect IP students?',
      answer:
        'IP students bypass the national SEC exam (which replaces all three national secondary exams from 2027) and proceed directly to A-Levels or the IB. Their internal school assessments serve the same purpose — Sec 2 IP results shape Upper Secondary subject options and pacing.',
    },
  ],
  '3-ip': [
    {
      question: 'What does Sec 3 IP Math typically cover?',
      answer:
        'Sec 3 IP typically covers polynomials and factor theorem, exponential and logarithmic functions, trigonometric identities and equations, introduction to calculus (differentiation), sequences and series, and vectors — often at or beyond A-Math depth.',
    },
    {
      question: 'How does Sec 3 IP Math differ from G3 with A-Math?',
      answer:
        'IP schools often cover similar or more advanced content than G3 A-Math but at a faster pace and with greater emphasis on conceptual understanding and proof. Some schools introduce H2 Math topics in Sec 3.',
    },
    {
      question: 'Is Sec 3 when IP Math gets significantly harder?',
      answer:
        'Yes. Sec 3 is widely considered the most demanding year for IP Math students. Topics like calculus, trigonometric proofs, and series require a level of mathematical maturity that many students have not yet developed.',
    },
    {
      question:
        "My child's IP school has a unique syllabus — can DeepThink adapt?",
      answer:
        "Yes. We adapt to each school's scheme of work and assessment calendar. Students bring their school materials and we align support to their specific curriculum, not a generic syllabus.",
    },
    {
      question:
        'Should my Sec 3 IP child already be preparing for A-Level H2 Math?',
      answer:
        'Sec 3 IP content is already building towards H2 Math readiness. The priority should be mastering current topics deeply rather than rushing ahead — strong Sec 3 foundations make JC Math significantly more manageable.',
    },
    {
      question: 'Does the SEC exam affect Sec 3 IP students?',
      answer:
        'IP students bypass the national SEC exam (which replaces all three national secondary exams from 2027) and proceed directly to A-Levels or the IB. However, internal school assessments in Sec 3 heavily influence Upper Secondary subject combinations and pacing for H2 Math.',
    },
  ],
  '4-ip': [
    {
      question: 'What does Sec 4 IP Math typically cover?',
      answer:
        'Sec 4 IP typically covers integration and its applications, advanced functions, parametric equations, probability distributions, permutations and combinations, and mathematical proofs — often reaching early H2 Math level.',
    },
    {
      question: 'Do Sec 4 IP students sit the O-Level Math exam?',
      answer:
        'Most IP students do not sit O-Levels. Instead, they take internal school assessments that determine their readiness for JC and H2 Math. Performance in these assessments shapes their JC subject combinations.',
    },
    {
      question: 'Is Sec 4 IP Math harder than O-Level A-Math?',
      answer:
        'Generally yes. IP Sec 4 content often reaches beyond the O-Level A-Math syllabus, with topics like probability distributions and mathematical induction that are normally H2 Math content. The depth and pace are both more demanding.',
    },
    {
      question: 'Should my Sec 4 IP child be preparing for H2 Math already?',
      answer:
        'In many ways, they already are — Sec 4 IP content is designed as a bridge to H2 Math. The priority is mastering current topics deeply, as H2 Math builds directly on Sec 4 IP foundations.',
    },
    {
      question:
        "My child's IP school results are declining in Sec 4 — what should we do?",
      answer:
        'Declining results in Sec 4 IP often indicate gaps from Sec 3 that are now compounding. Identify the specific weak topics (usually calculus or trigonometry) and address them directly, rather than attempting broad revision.',
    },
  ],
};

const IP_DECISION: Record<string, DecisionProofPoint[]> = {
  '1-ip': [
    {
      title:
        'Your child entered an IP school and is finding Math unexpectedly hard',
      description:
        'IP schools expect faster learning, deeper understanding, and more independent problem-solving from day one. Even academically strong students may need support adjusting to this environment.',
    },
    {
      title: "Your child's school does not follow a standard textbook",
      description:
        'Many IP schools use custom materials and non-standard pacing. This makes it harder for parents to support at home and harder for generic tuition to help. School-aligned support is essential.',
    },
    {
      title:
        'You want to ensure your child stays on track without waiting for problems to appear',
      description:
        'In IP programmes, gaps compound faster due to the accelerated pace. Proactive weekly support keeps students ahead of difficulties rather than reacting to declining results.',
    },
  ],
  '2-ip': [
    {
      title: "Your child's IP school is moving faster than they can absorb",
      description:
        'IP Sec 2 often introduces Sec 3-level content. Students who managed Sec 1 comfortably may find the acceleration unsustainable without support to reinforce concepts between lessons.',
    },
    {
      title:
        'Sec 2 assessments determine subject combinations for Upper Secondary',
      description:
        'Many IP schools use Sec 2 results to finalise subject combinations. Consistent Math performance in Sec 2 ensures your child has the widest possible options for Sec 3-4.',
    },
    {
      title:
        'Your child is struggling with the depth expected, not just the content',
      description:
        'IP schools reward mathematical reasoning and proof-style thinking, not just correct answers. If your child can calculate but cannot explain why, targeted coaching builds this deeper skill.',
    },
  ],
  '3-ip': [
    {
      title:
        'Sec 3 IP Math has introduced calculus and your child is struggling',
      description:
        'Calculus is conceptually different from anything students have encountered before. Many IP students need additional support to build intuition for rates of change and curve analysis.',
    },
    {
      title:
        "Your child's school assessments are becoming harder to prepare for independently",
      description:
        'IP Sec 3 assessments often include non-standard problems and proof-style questions. Students benefit from guided exposure to these question types, not just more practice of familiar ones.',
    },
    {
      title: 'You want your child prepared for H2 Math before JC starts',
      description:
        'Sec 3 IP builds the foundations that H2 Math assumes are in place. Gaps from Sec 3 become serious obstacles in JC, where the pace does not allow time for remediation.',
    },
  ],
  '4-ip': [
    {
      title:
        "Your child's Sec 4 IP assessment results will determine H2 Math eligibility",
      description:
        'Most IP schools require strong Sec 4 Math results for H2 Math placement in JC. Underperformance may result in being placed in H1 Math, which limits university course options.',
    },
    {
      title: 'Sec 3 gaps are now making Sec 4 topics much harder',
      description:
        'Calculus integration, probability distributions, and advanced functions all build on Sec 3 foundations. If those foundations are weak, Sec 4 topics feel impossible without targeted remediation.',
    },
    {
      title: 'Your child needs to bridge to JC-level expectations',
      description:
        'Sec 4 IP is the bridge to H2 Math. Students who finish Sec 4 with strong foundations in calculus, trigonometry, and proof techniques find JC Math challenging but manageable — those without these foundations find it overwhelming.',
    },
  ],
};

export function buildSecondaryIpContent(
  secLevel: number,
  brandName = 'DeepThink'
): LevelPageContent {
  const content = buildStreamPageContent(
    secLevel,
    IP_SEMANTICS,
    'ip',
    brandName
  );
  const dataKey = `${secLevel}-ip`;
  return {
    ...content,
    ...(IP_FAQS[dataKey] && { faqItems: IP_FAQS[dataKey] }),
    ...(IP_DECISION[dataKey] && {
      decisionProofPoints: IP_DECISION[dataKey],
    }),
  };
}
